<template>
    <ExceptionExpirationModal
        :exception-inputs="exception.data"
        :requirement="requirement"
        :loading="loading"
        :note="note"
        @close="close"
        @grant-exception="grantException"
        @update:note="note = $event"
    />
</template>

<style lang="scss">
    .GrantExceptionProcedureModal {
        .Modal__body {
            max-width: 650px;
        }

        .Modal__content {
            display: inline-flex;
            gap: 40px;
            flex-direction: column;
            align-items: flex-start;
        }

        .Modal__footer {
            flex-direction: row-reverse;
            gap: 20px;
            margin: 20px 0 40px 0;
        }

        &__line {
            text-align: start;
        }
    }
</style>

<script lang="ts">
    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import {
        ExceptionInput,
    } from '@evidentid/tprm-portal-lib/models/entity-details';
    import { EntityRequirement } from '@evidentid/tprm-portal-lib/models/entity-details/EntityRequirement.model';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import { Exception } from '@/modules/dashboard/types';
    import ExceptionExpirationModal
        from '@/modules/entity-details/components/ExceptionExpirationModal/ExceptionExpirationModal.vue';
    import { useEntityDetailsStore } from '@/modules/entity-details/store';

    // PRODUCT-26110 QA fixes4: declares every event this procedure component `$emit`s --
    // without a declared `emits`, Vue 3 warns "Component emitted event 'abort'/'finish'
    // but it is neither declared in the emits option nor as an 'onAbort'/'onFinish' prop"
    // every time it fires.
    @Component({
        components: { ExceptionExpirationModal },
        emits: [ 'abort', 'finish' ],
    })
    export default class GrantExceptionProcedure extends Vue {
        // PRODUCT-26110 QA fixes4: ProcedureTarget.vue passes `:uuid="execution.uuid"` to
        // every dynamically-rendered procedure component; no procedure consumes the
        // value, it's declared here purely so Vue 3 doesn't warn "Extraneous non-props
        // attributes (uuid)" (see UploadEntityDocumentProcedure.vue for the full note).
        @Prop({ type: String, default: '' })
        private uuid?: string;

        @Prop({ type: Object, default: () => ({}) })
        private exception!: Exception;

        @Prop({ type: Object, default: () => ({}) })
        private requirement!: EntityRequirement | null;

        private exceptionTypeSelected: boolean = false;
        private exceptionToGrant = this.exception;
        private note: string = '';

        private get loading(): boolean {
            return (
                useEntityDetailsStore().grantExceptionStatus[this.exception.rpName] === OperationStatus.loading
            );
        }

        private async grantException(exceptionInputs: ExceptionInput[]): Promise<void> {
            const inputsWithNote = exceptionInputs.map((item) => ({
                ...item,
                note: this.note || undefined,
            }));
            this.exceptionToGrant = { ...this.exception, data: inputsWithNote };
            this.exceptionTypeSelected = true;
            await useEntityDetailsStore().grantExceptions({
                rpName: this.exceptionToGrant.rpName,
                entityId: this.exceptionToGrant.insured.id,
                exceptions: this.exceptionToGrant.data,
            });
            const grantExceptionStatus =
                useEntityDetailsStore().grantExceptionStatus[this.exceptionToGrant.rpName];
            if (grantExceptionStatus === OperationStatus.success) {
                this.$emit('finish', this.exceptionToGrant.insured.displayName);
            } else {
                this.$emit('abort');
            }
        }

        private close(): void {
            this.$emit('abort');
        }
    }
</script>
